import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import type {
  CertMatrix,
  CertMatrixCell,
  CertMatrixQuery,
  CertCategoryRow,
  CrewDetail,
  WageClassificationRow,
} from '@/api/types'
import {
  OUTCOME_LABEL,
  STATE_LABEL,
  badgeClass,
  dayLabel,
  outcomeTone,
  stateTone,
  useCertAccess,
} from '@/lib/certs'

/**
 * Certifications — everyone in the field against every active cert type.
 *
 * The matrix answers the office's two questions at a glance: who is missing
 * something they are required to hold, and what lapses next. Each cell shows
 * the WORST thing true of it, because that is the thing to act on:
 *
 *   Missing   required of this person and nothing on file
 *   Expired / Blocked / Re-eval / Pending / Soon   the held card's state
 *   OK        current
 *   Equiv     not held, but a requirement is met by an equivalent (OSHA 30
 *             for OSHA 10)
 *   blank     not held and not required
 *
 * Status is computed by the server at the date shown — nothing here is
 * stored, so a card that lapsed overnight reads Expired on the next load.
 *
 * Restricted (medical) columns carry the outcome only below Admin: no dates.
 */

type StateFilter = NonNullable<CertMatrixQuery['state']>

const SHORT: Record<string, string> = {
  Valid: 'OK',
  Expiring: 'Soon',
  Expired: 'Expired',
  BlockedByPrereq: 'Blocked',
  Reevaluate: 'Re-eval',
  Pending: 'Pending',
}

/** What one cell shows: the worst true thing about it. */
function cellView(c: CertMatrixCell): { text: string; cls: string; title: string } | null {
  const parts: string[] = []
  if (c.cert_state) {
    parts.push(STATE_LABEL[c.cert_state])
    if (c.expires_date) parts.push(`expires ${dayLabel(c.expires_date)}`)
  }
  if (c.requirement_outcome) {
    parts.push(
      `${c.requirement_count === 1 ? 'Required' : `Required (${c.requirement_count} rules)`}: ` +
        OUTCOME_LABEL[c.requirement_outcome],
    )
  }
  const title = parts.join(' — ')

  if (c.requirement_outcome === 'Missing') {
    return { text: 'Missing', cls: badgeClass('error'), title }
  }
  if (c.cert_state) {
    return { text: SHORT[c.cert_state], cls: badgeClass(stateTone(c.cert_state)), title }
  }
  if (c.requirement_outcome === 'InGracePeriod') {
    return { text: 'Grace', cls: badgeClass('warn'), title }
  }
  if (c.requirement_outcome) {
    // Met through another type, or an equivalent that is itself expiring.
    return {
      text: c.requirement_outcome === 'MetByEquivalent' ? 'Equiv' : SHORT[c.requirement_outcome] ?? '·',
      cls: badgeClass(outcomeTone(c.requirement_outcome)),
      title,
    }
  }
  return null
}

function csvEscape(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v
}

export function CertificationsPage() {
  const access = useCertAccess()
  const navigate = useNavigate()

  const [matrix, setMatrix] = useState<CertMatrix | null>(null)
  // The grid scrolls both ways inside its own box. The box ends at the bottom
  // of the window, so its horizontal scrollbar is always on screen -- at a
  // fixed 70vh it started below the filters and the bar was off the bottom.
  const wrapRef = useRef<HTMLDivElement>(null)
  const [wrapHeight, setWrapHeight] = useState<number | string>('70vh')
  // True while columns are hidden off the right edge: says so in words, since
  // a hidden scrollbar is no sign at all.
  const [moreRight, setMoreRight] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [categories, setCategories] = useState<CertCategoryRow[]>([])
  const [crews, setCrews] = useState<CrewDetail[]>([])
  const [classes, setClasses] = useState<WageClassificationRow[]>([])

  const [asOf, setAsOf] = useState('')
  const [categoryId, setCategoryId] = useState<number | ''>('')
  const [state, setState] = useState<StateFilter | ''>('')
  const [supervisorId, setSupervisorId] = useState<number | ''>('')
  const [classId, setClassId] = useState<number | ''>('')
  const [location, setLocation] = useState('')
  const [search, setSearch] = useState('')

  const load = useCallback(async () => {
    setError(null)
    try {
      setMatrix(
        await api.certMatrix({
          as_of: asOf || undefined,
          cert_category_id: categoryId === '' ? undefined : categoryId,
          state: state || undefined,
          supervisor_employee_id: supervisorId === '' ? undefined : supervisorId,
          wage_classification_id: classId === '' ? undefined : classId,
          location: location || undefined,
        }),
      )
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 404
          ? 'Certifications are not switched on for your account.'
          : err instanceof Error
            ? err.message
            : 'Could not load the matrix',
      )
    }
  }, [asOf, categoryId, state, supervisorId, classId, location])

  useEffect(() => {
    void load()
  }, [load])

  // Filter lists. Each is optional: a list that fails to load just leaves its
  // filter empty rather than breaking the matrix.
  useEffect(() => {
    void api.certCategories().then(setCategories).catch(() => setCategories([]))
    void api.adminCrews().then(setCrews).catch(() => setCrews([]))
    void api.wageClassifications().then(setClasses).catch(() => setClasses([]))
  }, [])

  const supervisorName = useMemo(
    () => new Map(crews.map((c) => [c.supervisor_employee_id, c.supervisor_name])),
    [crews],
  )
  const locations = useMemo(
    () => [...new Set((matrix?.rows ?? []).map((r) => r.location).filter(Boolean) as string[])].sort(),
    [matrix],
  )

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    const all = matrix?.rows ?? []
    if (!q) return all
    return all.filter(
      (r) => r.display_name.toLowerCase().includes(q) || r.employee_no.toLowerCase().includes(q),
    )
  }, [matrix, search])

  // Counts over what is shown, by the same rule the cells use.
  const counts = useMemo(() => {
    const n = { Missing: 0, Expired: 0, Blocked: 0, Soon: 0 }
    for (const r of rows) {
      for (const c of r.cells) {
        if (c.requirement_outcome === 'Missing') n.Missing++
        else if (c.cert_state === 'Expired') n.Expired++
        else if (c.cert_state === 'BlockedByPrereq' || c.cert_state === 'Reevaluate') n.Blocked++
        else if (c.cert_state === 'Expiring') n.Soon++
      }
    }
    return n
  }, [rows])

  function exportCsv() {
    if (!matrix) return
    const head = ['Employee no', 'Name', ...matrix.columns.map((c) => c.short_name)]
    const lines = [head.map(csvEscape).join(',')]
    for (const r of rows) {
      const byType = new Map(r.cells.map((c) => [c.cert_type_id, c]))
      const cells = matrix.columns.map((col) => {
        const c = byType.get(col.cert_type_id)
        const v = c ? cellView(c) : null
        if (!v) return ''
        return c?.expires_date ? `${v.text} ${c.expires_date}` : v.text
      })
      lines.push([r.employee_no, r.display_name, ...cells].map(csvEscape).join(','))
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `certifications-${matrix.as_of}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  useLayoutEffect(() => {
    const fit = () => {
      const el = wrapRef.current
      if (!el) return
      const top = el.getBoundingClientRect().top + window.scrollY
      setWrapHeight(Math.max(320, window.innerHeight - top - 24))
      setMoreRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 4)
    }
    fit()
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [matrix])

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="crew-heading">
          <h2 style={{ margin: 0 }}>Employee certifications</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" disabled={!matrix} onClick={exportCsv}>
              Export CSV
            </button>
          </div>
        </div>
        <p className="muted" style={{ marginBottom: 0 }}>
          Field staff against every active certification
          {matrix ? `, as of ${dayLabel(matrix.as_of)}` : ''}. Each cell shows the most urgent
          thing about it; hover for the detail. Medical certifications show the outcome only.
          Select a person to record or renew a card.
        </p>
      </div>

      <div className="card">
        <div className="field-grid">
          <div className="form-field">
            <label htmlFor="certSearch">Person</label>
            <input
              id="certSearch"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name or F-number"
            />
          </div>
          <div className="form-field">
            <label htmlFor="certState">Show</label>
            <select id="certState" value={state} onChange={(e) => setState(e.target.value as StateFilter | '')}>
              <option value="">Everyone</option>
              <option value="Missing">Missing a required cert</option>
              <option value="Expired">Has an expired cert</option>
              <option value="Blocked">Blocked or needs re-evaluation</option>
              <option value="Expiring">Expiring soon</option>
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="certCategory">Category</label>
            <select
              id="certCategory"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">All</option>
              {categories.filter((c) => c.is_active).map((c) => (
                <option key={c.cert_category_id} value={c.cert_category_id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="certCrew">Crew</label>
            <select
              id="certCrew"
              value={supervisorId}
              onChange={(e) => setSupervisorId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">All crews</option>
              {crews.map((c) => (
                <option key={c.supervisor_employee_id} value={c.supervisor_employee_id}>
                  {c.supervisor_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="certClass">Classification</label>
            <select
              id="certClass"
              value={classId}
              onChange={(e) => setClassId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">All</option>
              {classes.map((c) => (
                <option key={c.wage_classification_id} value={c.wage_classification_id}>
                  {c.description}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="certLocation">Location</label>
            <select id="certLocation" value={location} onChange={(e) => setLocation(e.target.value)}>
              <option value="">All</option>
              {locations.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>
          {access.can('use_as_of') && (
            <div className="form-field">
              <label htmlFor="certAsOf">As of</label>
              <input id="certAsOf" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
            </div>
          )}
        </div>

        {matrix && (
          <p className="muted" style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem' }}>
            {rows.length} {rows.length === 1 ? 'person' : 'people'} ·{' '}
            <span className={badgeClass(counts.Missing ? 'error' : 'none')}>{counts.Missing} missing</span>{' '}
            <span className={badgeClass(counts.Expired ? 'error' : 'none')}>{counts.Expired} expired</span>{' '}
            <span className={badgeClass(counts.Blocked ? 'error' : 'none')}>{counts.Blocked} blocked</span>{' '}
            <span className={badgeClass(counts.Soon ? 'warn' : 'none')}>{counts.Soon} expiring soon</span>
          </p>
        )}
      </div>

      <div className="card">
        {matrix === null ? (
          <p className="muted">{error ? '' : 'Loading…'}</p>
        ) : rows.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>Nobody matches these filters.</p>
        ) : (
          <>
          <p className="muted" style={{ margin: '0 0 0.5rem', fontSize: '0.8125rem', minHeight: '1.2em', textAlign: 'right' }}>
            {moreRight && (
              <button
                className="btn-plain"
                onClick={() => wrapRef.current?.scrollBy({ left: wrapRef.current.clientWidth * 0.8, behavior: 'smooth' })}
              >
                More certifications to the right →
              </button>
            )}
          </p>
          <div
            ref={wrapRef}
            className="table-wrap table-dense"
            style={{ maxHeight: wrapHeight, overflow: 'auto' }}
            onScroll={(e) => {
              const el = e.currentTarget
              setMoreRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 4)
            }}
          >
            <table>
              <thead>
                <tr>
                  <th style={{ position: 'sticky', left: 0, top: 0, zIndex: 2, background: 'var(--c-surface)' }}>
                    Person
                  </th>
                  {matrix.columns.map((col) => (
                    <th
                      key={col.cert_type_id}
                      title={`${col.name} (${col.category_name})${col.is_restricted ? ' — restricted' : ''}`}
                      style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--c-surface)', whiteSpace: 'nowrap' }}
                    >
                      {col.short_name}
                      {col.is_restricted && <span className="muted"> ᴿ</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const byType = new Map(r.cells.map((c) => [c.cert_type_id, c]))
                  return (
                    <tr
                      key={r.employee_id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/certs/people/${r.employee_id}`)}
                    >
                      <td style={{ position: 'sticky', left: 0, background: 'var(--c-surface)', whiteSpace: 'nowrap' }}>
                        <Link to={`/certs/people/${r.employee_id}`} onClick={(e) => e.stopPropagation()}>
                          {r.display_name}
                        </Link>{' '}
                        <span className="mono muted" style={{ fontSize: '0.75rem' }}>{r.employee_no}</span>
                        {r.supervisor_employee_id && supervisorName.get(r.supervisor_employee_id) && (
                          <div className="muted" style={{ fontSize: '0.75rem' }}>
                            {r.supervisor_employee_id === r.employee_id
                              ? 'Supervisor'
                              : `Crew: ${supervisorName.get(r.supervisor_employee_id)}`}
                          </div>
                        )}
                      </td>
                      {matrix.columns.map((col) => {
                        const c = byType.get(col.cert_type_id)
                        const v = c ? cellView(c) : null
                        return (
                          <td key={col.cert_type_id} title={v?.title} style={{ textAlign: 'center' }}>
                            {v && <span className={v.cls}>{v.text}</span>}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  )
}
